import { getSessionHeaders } from './lib/logger/client.js';
import type { GetPurchaseHistoryStreamResponse } from './types/purchase';
import type { InsightsResponse } from './types/insights';
import type { PostConnectSurveyQuestionsResponse } from './pages/post-connect-survey/types';
import {
  AmazonPurchase,
  SafewayPurchaseRow,
  WalmartPurchaseRow,
  DoorDashOrderRaw,
  TargetPurchaseRow,
  BestbuyPurchaseRow,
} from './lib/connector/index.js';

const API_BASE_URL = '/api';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

type SubmitDataResponse = {
  email?: string;
};

type FeatureFlagsResponse = Record<string, boolean | string[]>;
type CapacityResponse = {
  hasCapacity: boolean;
};

type DoorDashPurchaseSyncResponse = {
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed' | null;
  syncError?: string | null;
  purchases: DoorDashOrderRaw[];
  lastSeenId: number;
};

type WalmartPurchaseSyncResponse = {
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed' | null;
  syncError?: string | null;
  purchases: WalmartPurchaseRow[];
  lastSeenId: number;
};

type SafewayPurchaseSyncResponse = {
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed' | null;
  syncError?: string | null;
  purchases: SafewayPurchaseRow[];
  lastSeenId: number;
};

type AmazonPurchaseSyncResponse = {
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed' | null;
  syncError?: string | null;
  purchases: AmazonPurchase[];
  lastSeenId: number;
};

type TargetPurchaseSyncResponse = {
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed' | null;
  syncError?: string | null;
  purchases: TargetPurchaseRow[];
  lastSeenId: number;
};

type BestbuyPurchaseSyncResponse = {
  syncStatus: 'pending' | 'syncing' | 'completed' | 'failed' | null;
  syncError?: string | null;
  purchases: BestbuyPurchaseRow[];
  lastSeenId: number;
};

type PostConnectSurveyResponseRequest = {
  surveyVersion: string;
  response: Record<string, unknown>;
  submittedAtClient?: string;
};

type PostConnectSurveyResponseResponse = {
  saved: true;
  surveyResponseId: number;
  redirectUrl: string | null;
};

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const isFormData = options.body instanceof FormData;
    const response = await fetch(url, {
      ...options,
      headers: {
        // Let the browser set multipart boundary for FormData bodies.
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...getSessionHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      let result: {
        success?: boolean;
        message?: string;
        error?: string;
        code?: string;
      };
      try {
        result = await response.json();
      } catch {
        throw new ApiRequestError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      throw new ApiRequestError(
        result.message ||
          result.error ||
          `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        result.code
      );
    }

    const result: ApiResponse<T> = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }

    return result.data as T;
  }

  async getSigninId(
    retailerKey: string,
    region?: 'US' | 'CA'
  ): Promise<{ signin_id: string }> {
    return this.request<{ signin_id: string }>(
      `/${retailerKey}/signin?region=${region}`,
      {
        method: 'GET',
      }
    );
  }

  async getAmazonPurchaseHistory(
    retailerKey: string,
    region?: 'US' | 'CA'
  ): Promise<GetPurchaseHistoryStreamResponse | null> {
    const result = await this.request<{
      type?: 'auth';
      signin_id?: string;
      accepted?: boolean;
    }>(`/${retailerKey}/purchase-history?region=${region}`, { method: 'GET' });

    if (result?.type === 'auth' && result.signin_id) {
      return { type: 'auth', signin_id: result.signin_id };
    }

    return null;
  }

  async pollAmazonPurchaseSync(
    retailerKey: string,
    sinceId: number,
    region?: 'US' | 'CA'
  ): Promise<AmazonPurchaseSyncResponse> {
    return this.request<AmazonPurchaseSyncResponse>(
      `/${retailerKey}/purchase-sync?sinceId=${sinceId}&region=${region}`,
      { method: 'GET' }
    );
  }

  async getFeatureFlags(): Promise<FeatureFlagsResponse> {
    return this.request<FeatureFlagsResponse>('/features', {
      method: 'GET',
    });
  }

  async checkCapacity(): Promise<CapacityResponse> {
    const response = await fetch('/health', {
      method: 'GET',
      headers: {
        ...getSessionHeaders(),
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const json = (await response.json()) as { availableCapacity?: number };
    return { hasCapacity: (json.availableCapacity ?? 0) > 0 };
  }

  async captureRewardEmail(email: string): Promise<void> {
    await this.request<Record<string, never>>('/capture-reward-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async releaseSyncCapacity(): Promise<void> {
    await this.request<Record<string, never>>('/release-sync-capacity', {
      method: 'POST',
    });
  }

  async getInsights(): Promise<InsightsResponse> {
    return this.request<InsightsResponse>('/insights', {
      method: 'GET',
    });
  }

  async getQuestionProRedirectUrl(): Promise<{ redirectUrl: string | null }> {
    return this.request<{ redirectUrl: string | null }>(
      '/questionpro-redirect-url',
      { method: 'GET' }
    );
  }

  async checkPanelIdleTermination(): Promise<{
    terminate: boolean;
    url?: string;
  }> {
    return this.request<{ terminate: boolean; url?: string }>(
      '/panel-idle-check',
      { method: 'GET' }
    );
  }

  async getPostConnectSurveyQuestions(
    surveyId: string
  ): Promise<PostConnectSurveyQuestionsResponse> {
    return this.request<PostConnectSurveyQuestionsResponse>(
      `/post-connect-survey-questions?surveyId=${encodeURIComponent(surveyId)}`,
      { method: 'GET' }
    );
  }

  async submitPostConnectSurveyResponse(
    params: PostConnectSurveyResponseRequest
  ): Promise<PostConnectSurveyResponseResponse> {
    return this.request<PostConnectSurveyResponseResponse>(
      '/post-connect-survey-response',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  }

  async uploadKrogerMembership(
    membershipNumber: string,
    photo: File
  ): Promise<{ saved: boolean; redirectUrl: string | null }> {
    const formData = new FormData();
    formData.append('membershipNumber', membershipNumber);
    formData.append('photo', photo);
    return this.request<{ saved: boolean; redirectUrl: string | null }>(
      '/kroger-membership-upload',
      {
        method: 'POST',
        body: formData,
      }
    );
  }

  async updateProxyIpAddress() {
    return this.request('/update-proxy-ip-address', {
      method: 'POST',
    });
  }

  async verifyQuestionProToken(
    token: string | null | undefined
  ): Promise<boolean> {
    if (!token || typeof token !== 'string') return false;
    const url = `${this.baseUrl}/verify-questionpro-token`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (response.ok) {
      const json = (await response.json()) as ApiResponse<{ valid: boolean }>;
      return json.success === true && json.data?.valid === true;
    }
    return false;
  }

  async submitData({
    totalUpcomingDeliveries,
    totalPastOrders,
    isConsentGiven = true,
  }: {
    totalUpcomingDeliveries: number;
    totalPastOrders: number;
    isConsentGiven?: boolean;
  }): Promise<SubmitDataResponse> {
    return this.request<SubmitDataResponse>('/submit-data', {
      method: 'POST',
      body: JSON.stringify({
        totalUpcomingDeliveries,
        totalPastOrders,
        isConsentGiven,
      }),
    });
  }

  async signin(
    signinId: string,
    body: Record<string, unknown>
  ): Promise<
    | {
        type: 'SUCCESS';
        panelEligibility?: {
          eligible: boolean;
          redirectUrl: string | null;
        };
      }
    | { type: 'NEED_SIGNIN' }
    | { type: 'ERROR'; error: string }
  > {
    return this.request(`/signin/${signinId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async tapResearchPlayerLookup(
    player: string
  ): Promise<{ hasCredentials: boolean }> {
    return this.request<{ hasCredentials: boolean }>(
      '/tap-research/player-lookup',
      {
        method: 'POST',
        body: JSON.stringify({ player }),
      }
    );
  }

  async getSafewaySignin(): Promise<
    { signin_id: string } | { safeway_orders: SafewayPurchaseRow[] }
  > {
    return this.request<
      { signin_id: string } | { safeway_orders: SafewayPurchaseRow[] }
    >('/safeway/signin', { method: 'GET' });
  }

  async getSafewayPurchaseHistory(): Promise<void> {
    const url = `${this.baseUrl}/safeway/purchase-history`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getSessionHeaders(),
      },
    });

    if (response.status === 202) {
      return;
    }

    if (!response.ok) {
      let result: {
        success?: boolean;
        message?: string;
        error?: string;
        code?: string;
      };
      try {
        result = await response.json();
      } catch {
        throw new ApiRequestError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      throw new ApiRequestError(
        result.message ||
          result.error ||
          `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        result.code
      );
    }

    const result: ApiResponse<unknown> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }
  }

  async pollSafewayPurchaseSync(
    sinceId: number
  ): Promise<SafewayPurchaseSyncResponse> {
    return this.request<SafewayPurchaseSyncResponse>(
      `/safeway/purchase-sync?sinceId=${sinceId}`,
      { method: 'GET' }
    );
  }

  async getWalmartSignin(): Promise<
    { signin_id: string } | Record<string, unknown>
  > {
    return this.request<{ signin_id: string } | Record<string, unknown>>(
      '/walmart/signin',
      { method: 'GET' }
    );
  }

  async getWalmartPurchaseHistory(): Promise<void> {
    const url = `${this.baseUrl}/walmart/purchase-history`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getSessionHeaders(),
      },
    });

    if (response.status === 202) {
      return;
    }

    if (!response.ok) {
      let result: {
        success?: boolean;
        message?: string;
        error?: string;
        code?: string;
      };
      try {
        result = await response.json();
      } catch {
        throw new ApiRequestError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      throw new ApiRequestError(
        result.message ||
          result.error ||
          `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        result.code
      );
    }

    const result: ApiResponse<unknown> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }
  }

  async pollWalmartPurchaseSync(
    sinceId: number
  ): Promise<WalmartPurchaseSyncResponse> {
    return this.request<WalmartPurchaseSyncResponse>(
      `/walmart/purchase-sync?sinceId=${sinceId}`,
      { method: 'GET' }
    );
  }

  async getDoorDashSignin(): Promise<
    { signin_id: string } | Record<string, never>
  > {
    return this.request<{ signin_id: string } | Record<string, never>>(
      '/doordash/signin',
      { method: 'GET' }
    );
  }

  async getDoorDashPurchaseHistory(): Promise<void> {
    const url = `${this.baseUrl}/doordash/purchase-history`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getSessionHeaders(),
      },
    });

    if (response.status === 202) {
      return;
    }

    if (!response.ok) {
      let result: {
        success?: boolean;
        message?: string;
        error?: string;
        code?: string;
      };
      try {
        result = await response.json();
      } catch {
        throw new ApiRequestError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      throw new ApiRequestError(
        result.message ||
          result.error ||
          `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        result.code
      );
    }

    const result: ApiResponse<unknown> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }
  }

  async pollDoorDashPurchaseSync(
    sinceId: number
  ): Promise<DoorDashPurchaseSyncResponse> {
    return this.request<DoorDashPurchaseSyncResponse>(
      `/doordash/purchase-sync?sinceId=${sinceId}`,
      { method: 'GET' }
    );
  }

  async ensureUser(params: {
    campaignId?: string;
    token?: string;
    tid?: string;
    player?: string;
    rid?: string;
    psid?: string;
    pid?: string;
    supplier_id?: string;
  }): Promise<{ userId: number }> {
    return this.request<{ userId: number }>('/ensure-user', {
      method: 'POST',
      body: JSON.stringify({
        campaign_id: params.campaignId,
        token: params.token,
        tid: params.tid,
        player: params.player,
        rid: params.rid,
        psid: params.psid,
        pid: params.pid,
        supplier_id: params.supplier_id,
      }),
    });
  }

  async getTargetSignin(): Promise<
    { signin_id: string } | Record<string, unknown>
  > {
    return this.request<{ signin_id: string } | Record<string, unknown>>(
      '/target/signin',
      { method: 'GET' }
    );
  }

  async getTargetPurchaseHistory(): Promise<void> {
    const url = `${this.baseUrl}/target/purchase-history`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getSessionHeaders(),
      },
    });

    if (response.status === 202) {
      return;
    }

    if (!response.ok) {
      let result: {
        success?: boolean;
        message?: string;
        error?: string;
        code?: string;
      };
      try {
        result = await response.json();
      } catch {
        throw new ApiRequestError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      throw new ApiRequestError(
        result.message ||
          result.error ||
          `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        result.code
      );
    }

    const result: ApiResponse<unknown> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }
  }

  async pollTargetPurchaseSync(
    sinceId: number
  ): Promise<TargetPurchaseSyncResponse> {
    return this.request<TargetPurchaseSyncResponse>(
      `/target/purchase-sync?sinceId=${sinceId}`,
      { method: 'GET' }
    );
  }

  async getBestbuySignin(): Promise<
    { signin_id: string } | Record<string, unknown>
  > {
    return this.request<{ signin_id: string } | Record<string, unknown>>(
      '/bestbuy/signin',
      { method: 'GET' }
    );
  }

  async getBestbuyPurchaseHistory(): Promise<void> {
    const url = `${this.baseUrl}/bestbuy/purchase-history`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getSessionHeaders(),
      },
    });

    if (response.status === 202) {
      return;
    }

    if (!response.ok) {
      let result: {
        success?: boolean;
        message?: string;
        error?: string;
        code?: string;
      };
      try {
        result = await response.json();
      } catch {
        throw new ApiRequestError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status
        );
      }
      throw new ApiRequestError(
        result.message ||
          result.error ||
          `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        result.code
      );
    }

    const result: ApiResponse<unknown> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }
  }

  async pollBestbuyPurchaseSync(
    sinceId: number
  ): Promise<BestbuyPurchaseSyncResponse> {
    return this.request<BestbuyPurchaseSyncResponse>(
      `/bestbuy/purchase-sync?sinceId=${sinceId}`,
      { method: 'GET' }
    );
  }

  async logInitialParams(params: {
    cid?: string;
    tid?: string;
    player?: string;
    region?: string;
    hasToken?: boolean;
    token?: string;
    rid?: string;
    psid?: string;
    pid?: string;
    supplier_id?: string;
    rsid?: string;
    participant_id?: string;
    study_id?: string;
  }): Promise<void> {
    try {
      await this.request('/log-initial-params', {
        method: 'POST',
        body: JSON.stringify(params),
      });
    } catch {
      // best-effort, don't block app load
    }
  }

  async analytics(
    event?: string,
    // oxlint-disable-next-line typescript/no-explicit-any
    properties: Record<string, any> = {},
    identify = false
  ): Promise<void> {
    try {
      await this.request('/analytics', {
        method: 'POST',
        body: JSON.stringify({ event, properties, identify }),
      });
      // oxlint-disable-next-line no-unused-vars
    } catch (error) {
      // do nothing
    }
  }
}

export const apiClient = new ApiClient();
